(ns dre.components.sankey-diagram
  (:require
   [applied-science.js-interop :as j]
   ["d3" :as d3]
   ["d3-sankey" :as d3-sankey]
   [dre.utils.bem :as bem]
   [reagent.core :as r]
   [goog.dom :as gdom]
   ["react-sizeme" :as react-sizeme]))

(def component-name :sankey-diagram)

(defn diagram
  [element data select-fn selected w h]
  (let [d3-el (j/call d3 :select element)
        svg (.. (j/call d3-el :append "svg")
                (attr "fill" "#d046ff"))
        sankey (.. (j/call d3-sankey :sankey)
                   (nodeId #(j/get % :name))
                   (nodeWidth 10)
                   (nodePadding 15)
                   (extent (into-array [(into-array [1 5]) (into-array [(- w 1) (- h 5)])])))
        formatNumber (j/call d3 :format ",.0f")
        data-copy data
        _ (sankey data-copy)
        rect (.. svg
                 (append "g")
                 (attr "stoke" "#000")
                 (selectAll)
                 (data (j/get data-copy :nodes))
                 (join "rect")
                 (attr "x" #(j/get % :x0))
                 (attr "y" #(j/get % :y0))
                 (attr "height" (fn [d] (- (j/get d :y1) (j/get d :y0))))
                 (attr "width" (fn [d] (- (j/get d :x1) (j/get d :x0))))
                 (attr "fill" (fn [d] "#969696")))
        link (.. svg
                 (append "g")
                 (attr "fill" "none")
                 (attr "stroke-opacity" 0.5)
                 (selectAll)
                 (data (j/get data-copy :links))
                 (join "g")
                 (style "mix-blend-mode" "multiply"))
        path (.. link
                 (append "path")
                 (attr "d" (j/call d3-sankey :sankeyLinkHorizontal))
                 (attr "stroke" "#9c9c9c")
                 (attr "stroke-opacity" 0.15)
                 (attr "stroke-width" (fn [d] (.max js/Math 1 (j/get d :width)))))]
    (.. svg
        (attr "width" w)
        (attr "height" h)
        (attr "style" "max-width: 100%; height: auto;")
        (attr "viewBox" (into-array [0 0 w h])))
    (.. rect
        (append "title")
        (text (fn [d] (str
                       (if (j/get d :label)
                         (j/get d :label)
                         (j/get d :name))
                       " "
                       (formatNumber (j/get d :value))))))
    (.. rect
        (on "mouseover" (fn [e d]
                          (.. (j/call path :classed "active" (fn [l] (or (= (j/get l :source) d)
                                                                         (= (j/get l :target) d))))
                              (order))
                          (.. (j/call rect :classed "active" (fn [l] (= l d)))
                              (order))))
        (on "mouseout" (fn [e d] (.. (j/call path :classed "active" false)
                                     (order))
                         (.. (j/call rect :classed "active" false)
                             (order))))
        (on "click" (fn [e d]
                      (if (not= (j/get-in d [:type])
                                "other")
                        (select-fn d)
                        nil))))
    (.. path
        (on "mouseover" (fn [e d]
                          (when (not= (j/get-in d [:target :type])
                                      "other")
                            (.. (j/call path :classed "active" (fn [l] (= l d)))
                                (order)))))
        (on "mouseout" (fn [e d] (.. (j/call path :classed "active" false)
                                     (order))))
        (on "click" (fn [e d]
                      (if (not= (j/get-in d [:target :type])
                                "other")
                        (select-fn d)
                        nil))))
    (.. rect
        (classed "other" (fn [l] (= (j/get l :type) "other")))
        (order))

    (.. path
        (classed "other" (fn [l] (= (j/get-in l [:target :type]) "other")))
        (order))

    (.. rect
        (attr "name" #(j/get % :name))
        (order))

    (.. path
        (attr "name" (fn [l] (str (j/get-in l [:source :name]) "-" (j/get-in l [:target :name]))))
        (order))

    (.. link
        (append "title")
        (text (fn [d] (str
                       (if (j/get-in d [:source :label])
                         (j/get-in d [:source :label])
                         (j/get-in d [:source :name]))
                       " → "
                       (if (j/get-in d [:target :label])
                         (j/get-in d [:target :label])
                         (j/get-in d [:target :name]))
                       "\n"
                       (formatNumber (j/get d :value))))))
    (.. svg
        (append "g")
        (selectAll)
        (data (j/get data-copy :nodes))
        (join "text")
        (attr "x" (fn [d] (if (< (j/get d :x0) (/ w 2))
                            (+ (j/get d :x1) 6)
                            (- (j/get d :x1) 16))))
        (attr "y" (fn [d] (/ (+ (j/get d :y1) (j/get d :y0)) 2)))
        (attr "dy" "0.35em")
        (attr "text-anchor" (fn [d] (if (< (j/get d :x0) (/ w 2))
                                      "start"
                                      "end")))
        (style "fill" "black")
        (text (fn [d] (if (j/get d :label)
                        (j/get d :label)
                        (j/get d :name)))))
    (.. selected
        (forEach (fn [d]
                   (if (.isArray js/Array d)
                     (.. (j/call d3 :selectAll (str "path[name='" (first d) "-" (second d) "']"))
                         (classed "selected" true))
                     (.. (j/call d3 :selectAll (str "rect[name='" d "']"))
                         (classed "selected" true))))))))

(defn sankey-diagram*
  [data select-fn selected width height]
  (let [element (atom nil)
        destroy!
        (fn [_this]
          (when @element
            (gdom/removeChildren @element)))
        repaint!
        (fn [this data select-fn selected width height]
          (destroy! this)
          (diagram @element data select-fn selected width height))]
    (r/create-class
     {:component-did-mount
      (fn [this]
        (repaint! this data select-fn selected width height))
      :component-will-unmount
      (fn [this]
        (destroy! this))
      :component-will-update
      (fn [this [_ data select-fn selected width height]]
        (repaint! this data select-fn selected width height))
      :reagent-render
      (fn [data select-fn selected width height]
        [:div
         {:style
          {:width "100%"
           :height "100%"}}
         [:div
          {:ref #(reset! element %)
           :style
           {:width "100%"
            :height "100%"}}]])})))

(defn sankey-diagram
  [data select-fn selected]
  [:div {:class component-name}
   [(r/adapt-react-class react-sizeme/SizeMe)
    {:monitorWidth true :monitorHeight true}
    (fn [props]
      (let [props (js->clj props :keywordize-keys true)]
        (r/as-element [sankey-diagram*
                       data
                       select-fn
                       selected
                       (:width (:size props))
                       (:height (:size props))])))]])
